<template>
  <VaNavbar :class="['app-layout-navbar py-2 px-0', { 'app-layout-navbar--mobile': isMobile }]">
    <template #left>
      <div class="left">
        <Transition v-if="isMobile" name="icon-fade" mode="out-in">
          <VaIcon
            color="primary"
            :name="settings.isSidebarMinimized ? 'menu' : 'close'"
            size="20px"
            style="margin-top: 3px"
            @click="settings.isSidebarMinimized = !settings.isSidebarMinimized"
          />
        </Transition>
        <!-- row with client logo and name, left-aligned -->
        <div class="client-branding flex flex-row gap-3 items-center align-center">
          <!-- Default Labvigator logo when no client -->
          <RouterLink
            v-if="!currentClient"
            to="/"
            aria-label="Visit home page"
            class="client-branding__link flex flex-row gap-3 items-center"
          >
            <img src="/icon/labvigator-landscape-clear.png" alt="Labvigator" class="h-8" />
          </RouterLink>
          <!-- Client logo and name when client is present -->
          <RouterLink
            v-if="currentClient && currentClient.clientName"
            :to="`/${currentClientUriSegment}`"
            aria-label="Visit client home page"
            class="client-branding__link flex flex-row gap-1 items-center"
          >
            <!-- Client logo (if available) -->
            <img
              v-if="currentClientLogoUrl"
              :src="currentClientLogoUrl"
              :alt="`${currentClient.clientName} logo`"
              class="h-8 sm:h-9 md:h-10 w-auto object-contain"
              @error="onLogoError"
            />
            <!-- Client name -->
            <span class="client-name ml-2 font-semibold text-base lg:text-lg">
              {{ currentClient?.clientName }}
            </span>
          </RouterLink>
          <span class="flex flex-row gap-3 items-center">
            <!-- title: Pathways LIS Management Portal  -->
            <!-- <h1 class="flex font-normal text-xl">Labvigator</h1> -->
            <!-- if pwRoles contains "pw:ADMIN", display a VaBadge/VaTag "PW Admin" -->
          </span>
        </div>
      </div>
    </template>
    <template v-if="!isMobile" #right>
      <div class="flex flex-row items-center gap-2">
        <VaBadge v-if="isPWAdmin" text="PW Admin" class="mr-2" />
        <div v-if="notPrdEnvironment" class="text-red-600 font-normal text-base md:text-lg lg:text-xl z-100">
          {{ environment }}
        </div>
        <AppNavbarActions class="app-navbar__actions" :is-mobile="isMobile" />
      </div>
    </template>
  </VaNavbar>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/global-store'
import AppNavbarActions from './components/AppNavbarActions.vue'
import { useMsal } from '../../azure/useMsal'
import { computed, ref, watch, onMounted } from 'vue'
import { useAppConfigStore } from '../../stores/app-config-store'
import { Client } from '../../interfaces/client'
import { useCurrentClient } from '../../composables/useCurrentClient'

defineProps({
  isMobile: { type: Boolean, default: false },
})

const GlobalStore = useGlobalStore()

const { settings } = storeToRefs(GlobalStore)

const { isPWAdmin } = useMsal()

// Check if environment is UAT
const environment = computed(() => {
  return import.meta.env.VITE_APP_ENVIRONMENT?.toUpperCase() ?? 'DEV'
})
const notPrdEnvironment = computed(() => {
  return environment.value !== 'PRD'
})

const { currentClientData, currentClientName, currentClientCode, currentClientLogoUrl, currentClientUriSegment } =
  useCurrentClient()

// For backward compatibility with existing template code
const currentClient = computed(() =>
  currentClientData.value
    ? ({
        clientCode: currentClientCode.value,
        clientName: currentClientName.value,
      } as Client)
    : null,
)

// Handle logo loading errors
const onLogoError = (event: Event) => {
  // Hide broken image by setting display to none
  const img = event.target as HTMLImageElement
  img.style.display = 'none'
}

// Debug logging
watch(
  () => currentClientData.value,
  (newData) => {
    console.log('Current client data changed:', newData)
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.va-navbar {
  z-index: 2;

  @media screen and (max-width: 950px) {
    .left {
      width: 100%;
    }

    .app-navbar__actions {
      display: flex;
      justify-content: space-between;
    }
  }
}

.left {
  display: flex;
  align-items: center;
  margin-left: 1rem;
  flex: 1 1 auto;
  min-width: 0;

  & > * {
    margin-right: 1rem;
  }

  & > *:last-child {
    margin-right: 0;
  }
}

:global(.va-navbar__left) {
  flex: 1 1 auto;
  min-width: 0;
}

:global(.app-layout-navbar--mobile) {
  display: flex;
  justify-content: flex-start;
}

:global(.app-layout-navbar--mobile .va-navbar__left) {
  width: 100%;
}

:global(.app-layout-navbar--mobile .va-navbar__center),
:global(.app-layout-navbar--mobile .va-navbar__right) {
  display: none;
}

.client-branding {
  flex: 1 1 auto;
  min-width: 0;
}

.client-branding__link {
  flex: 1 1 auto;
  min-width: 0;
}

.client-name {
  display: block;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: transform 0.5s ease;
}

.icon-fade-enter,
.icon-fade-leave-to {
  transform: scale(0.5);
}
</style>
